import React from 'react'
import styled from 'styled-components'
import { Swiper } from '@Components/Utilities/Swiper'
import ClassroomCard from '@Components/Cards/ClassroomCard'
import SkeletonCarousel from '@Components/Utilities/Skeleton/Components/SkeletonCarousel'
import SkeletonCard from '@Components/Utilities/Skeleton/Components/SkeletonCard'
import { useGetClassroomsQuery } from '@Features/LandingPage/LandingPageApi/LandingPageApi'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { trackEvent } from '@Utilities/marketingTags'
import { Container, MOBILE } from '../CustomLanding.styled'
import { classroomsSection, colors } from '../content'

export const CLASSROOMS_SECTION_ID = 'classrooms'

export const scrollToClassrooms = (ctaText: string) => {
	trackEvent('CTAClick', { cta_text: ctaText, cta_location: 'custom_landing' })
	document.getElementById(CLASSROOMS_SECTION_ID)?.scrollIntoView({ behavior: 'smooth' })
}

/* Shared gate for the section itself and for every CTA that scrolls to it:
   the tenant runs classrooms-only, but the section (and its CTAs) must still
   disappear cleanly if the flag is off or no classroom is published yet.
   RTK Query dedupes the repeated hook calls into one request. */
export const useClassroomsVisible = () => {
	const { classrooms_enabled } = useAppSelector(s => s.settings)
	const { data, isSuccess, isError } = useGetClassroomsQuery(undefined, { skip: !classrooms_enabled })
	if (!classrooms_enabled || isError) return false
	if (!isSuccess) return true
	return !!data && data.data.length > 0
}

const ClassroomsCarousel: React.FC = () => {
	const visible = useClassroomsVisible()
	const { classrooms_enabled } = useAppSelector(s => s.settings)
	const { data, isLoading, isSuccess } = useGetClassroomsQuery(undefined, { skip: !classrooms_enabled })

	if (!visible) return null

	return (
		<Section id={CLASSROOMS_SECTION_ID}>
			<Container>
				<Title>
					{classroomsSection.title}
					<span>{classroomsSection.titleEmoji}</span>
				</Title>
				<Sub>{classroomsSection.sub}</Sub>
			</Container>
			<Container>
				<SkeletonCarousel padding={0} gridGap={'1rem'} numberOfItems={3} isLoading={isLoading} renderComponent={<SkeletonCard />}>
					<SwiperWrapper>
						<Swiper perSlide={{ sm: 1 }} overlayArrows withArrows withProgress>
							{isSuccess &&
								data &&
								data.data.map((classroom, index) => (
									<Swiper.Item key={`${classroom.id}-${index}-custom-landing-classrooms`} swipe="1">
										<ClassroomCard
											title={classroom.title}
											instructor={`${classroom.instructor.user.first_name} ${classroom.instructor.user.last_name}`}
											badgeLabel={classroom.type}
											grade={((classroom.educationYears ?? []).length > 0 && classroom?.educationYears[0]?.name) || ''}
											subject={classroom.category.name}
											thumbnail={classroom.thumbnail}
											subjectImage={classroom.category.icon}
											classroom={classroom}
										/>
									</Swiper.Item>
								))}
						</Swiper>
					</SwiperWrapper>
				</SkeletonCarousel>
			</Container>
		</Section>
	)
}

export default ClassroomsCarousel

const Section = styled.section`
	background: #ffffff;
	padding: 64px 0;
	border-top: 1px solid rgba(22, 58, 132, 0.1);
	/* keeps the heading clear of the fixed app header when CTAs scroll here */
	scroll-margin-top: 6rem;
	${MOBILE} {
		padding: 40px 0;
	}
`

const Title = styled.h2`
	font-weight: 700;
	font-size: clamp(24px, 2.2vw, 34px);
	line-height: 1.35;
	color: ${colors.navy};
	margin: 0 0 20px;
	${MOBILE} {
		font-size: 28px;
		line-height: 1.4;
		margin-bottom: 14px;
	}
`

const Sub = styled.p`
	margin: 0 0 32px;
	font-size: 17px;
	line-height: 1.95;
	color: ${colors.body};
	${MOBILE} {
		font-size: 16px;
		line-height: 1.9;
		margin-bottom: 26px;
	}
`

const SwiperWrapper = styled.div`
	${MOBILE} {
		margin: 0 -1rem;
	}
`
